import {
  DataGrid as Grid,
  GridColumnProps,
  LinkColumn,
  SelectFilterComponent,
  SelectFloatingFilterComponent,
} from '@roc/ui';
import { observer } from 'mobx-react';
import React from 'react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { formatDate } from '@roc/feature-utils';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { useState } from 'react';
import { Box } from '@material-ui/core';
import { InformationDialog } from '@roc/ui';


const columns: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    maxWidth: 120,
    cellRenderer: 'loanIdCellRenderer',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    pinned: 'left',
    lockPinned: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 140,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'loanType',
    headerName: 'Loan Type',
    minWidth: 180,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'loanSubType',
    headerName: 'Loan Subtype',
    minWidth: 200,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'expectedClosingDate',
    headerName: 'Expected Closing Date',
    minWidth: 180,
    filter: 'agDateColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'taskName',
    headerName: 'Task Name',
    minWidth: 200,
    cellRenderer: 'taskNameCellRenderer',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'taskStatus',
    headerName: 'Task Status',
    minWidth: 160,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'assignee',
    headerName: 'Assignee',
    minWidth: 160,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'lenderName',
    headerName: 'Lender',
    minWidth: 160,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'underwriterName',
    headerName: 'Underwriter',
    minWidth: 160,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    minWidth: 160,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'lawFirm',
    headerName: 'Law Firm',
    minWidth: 160,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'primaryCsc',
    headerName: 'Primary CSC',
    minWidth: 160,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'creationDate',
    headerName: 'Creation Date',
    minWidth: 180,
    filter: 'agDateColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  }
];

export const ClosingConditionsTasksGrid = observer(() => {
  const { closingConditionsDashboardStore, globalStore } = useStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const { closingConditionsGridStore } = closingConditionsDashboardStore;
  const [isViewClosingConditionOpen, setIsViewClosingConditionOpen] = useState(false);
  const [selectedClosingCondition, setSelectedClosingCondition] = useState<any>(null);

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    loanIdCellRenderer: (params: any) => {
      return globalStore.isInternalPortal ?
        LinkColumn({
          ...params,
          onClick: () => { window.parent.postMessage({ loanId: params.node.data?.loanId }, '*'); },
        }) :
        LinkColumn({
          ...params,
          url: loanRoutesConfig.loans(params.value).children.dashboard.url,
        });
    },
    taskNameCellRenderer: (params: any) =>
      LinkColumn({
        ...params,
        url: '#',
        onClick: (e: any) => {
          e.preventDefault();
          setSelectedClosingCondition(params.node.data);
          setIsViewClosingConditionOpen(true);
        },
      }),
  };

  const onSortChanged = (params: any) => {
    const sortModel = params.api.getSortModel();
    closingConditionsGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = (params: any) => {
    const filterModel = params.api.getFilterModel();
    closingConditionsGridStore.setFilterModel(filterModel);
  };

  return (
    <>
      <Grid
        columns={columns}
        rows={closingConditionsGridStore.gridData.data.rows}
        frameworkComponents={frameworkComponents}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        isLoading={closingConditionsGridStore.isLoading}
        showFilters={closingConditionsGridStore.showFilters}
        sortModel={closingConditionsGridStore.sortModel}
        filterModel={closingConditionsGridStore.filterModel}
        paginationData={closingConditionsGridStore.gridData.meta}
        setPageNumber={closingConditionsGridStore.setPageNumber}
      />

      <InformationDialog
        open={isViewClosingConditionOpen}
        title="Closing Condition"
        handleClose={() => setIsViewClosingConditionOpen(false)}
        body={
          <Box>
            <strong>Title:</strong> {selectedClosingCondition?.closingConditionTitle} <br />
            <strong>Description:</strong> {selectedClosingCondition?.closingConditionDescription}
          </Box>
        }
      />
    </>
  );
});